import React, { useState, useEffect } from 'react'
import { Input, Button, message, Spin, Empty } from 'antd'
import DefaultProductImage from './DefaultProductImage'
import { useDebounce } from 'Hooks'

const { TextArea } = Input

interface Product {
  id?: string
  name?: string
  label?: string
  value?: string
  code?: string
  image?: string | null
  sku?: string
}

interface ProductTwoPanelSelectorProps {
  onSelect: (products: Product[]) => void
  selectedProducts: Product[]
  products: Product[]
  loading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onSearch: (keyword: string) => void
  operator?: string
  onCancel?: () => void
}

const STYLES = {
  container: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '4px',
  },
  importSection: {
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f9f9f9',
    borderRadius: '4px',
  },
  importLabel: {
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
  },
  importExample: {
    color: '#999',
    fontSize: '12px',
    marginBottom: '8px',
  },
  panelContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  panel: {
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    padding: '12px',
    height: '350px',
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#fff',
  },
  panelHeader: {
    fontWeight: 500,
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px',
  },
  searchBox: {
    marginBottom: '8px',
  },
  productList: {
    flex: 1,
    overflowY: 'auto' as const,
    paddingRight: '4px',
  },
  productItem: {
    padding: '6px 8px',
    cursor: 'pointer',
    borderRadius: '4px',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s',
    border: '1px solid transparent',
    fontSize: '13px',
  },
  productItemHover: {
    backgroundColor: '#f5f5f5',
  },
  productImage: {
    width: '24px',
    height: '24px',
    objectFit: 'cover' as const,
    borderRadius: '2px',
    backgroundColor: '#f0f0f0',
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontSize: '13px',
    lineHeight: '1.3',
    wordBreak: 'break-word' as const,
  },
  productId: {
    fontSize: '11px',
    color: '#999',
    marginTop: '2px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #f0f0f0',
  },
  footerButtons: {
    display: 'flex',
    gap: '8px',
  },
}

const ProductTwoPanelSelector: React.FC<ProductTwoPanelSelectorProps> = ({
  onSelect,
  selectedProducts: initialSelected,
  products,
  loading,
  hasMore,
  onLoadMore,
  onSearch,
  operator,
  onCancel,
}) => {
  const [importText, setImportText] = useState('')
  const [importLoading, setImportLoading] = useState(false)
  const [availableProducts, setAvailableProducts] = useState<Product[]>([])
  const [availableSearch, setAvailableSearch] = useState('')
  const debouncedAvailableSearch = useDebounce(availableSearch, 600)
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null)
  const [tempSelected, setTempSelected] = useState<Product[]>(initialSelected || [])

  // Update available products from props
  useEffect(() => {
    const selectedIds = new Set(tempSelected.map((p) => p.value || p.code || p.id))
    const newAvailable = products.filter((p) => !selectedIds.has(p.value || p.code || p.id))
    setAvailableProducts(newAvailable)
  }, [products, tempSelected])

  // Trigger search with debounced value
  useEffect(() => {
    onSearch(debouncedAvailableSearch)
  }, [debouncedAvailableSearch, onSearch])

  // Initialize with selected products
  useEffect(() => {
    setTempSelected(initialSelected || [])
  }, [initialSelected])

  const handleImport = async () => {
    if (!importText.trim()) {
      message.warning('Please enter product IDs')
      return
    }

    // Parse product IDs from text
    const productIds = importText
      .split(/[\n,\s]+/)
      .map((id) => id.trim())
      .filter((id) => id)

    if (productIds.length === 0) {
      message.warning('No valid product IDs found')
      return
    }

    setImportLoading(true)
    const foundProductsMap = new Map<string, Product>()
    const notFoundIds: string[] = []
    let searchCount = 0

    try {
      // Store products found for each search
      const searchResults: Product[][] = []

      // Search for each product ID
      for (const id of productIds) {
        // Trigger search with this ID
        onSearch(id)
        searchCount++

        // Wait for search to complete
        await new Promise((resolve) => setTimeout(resolve, 600))

        // Store current search results
        if (products && products.length > 0) {
          searchResults.push([...products])
        } else {
          searchResults.push([])
        }
      }

      // Process search results to find matching products
      productIds.forEach((id, index) => {
        const searchResult = searchResults[index] || []
        let found = false

        // First look for exact match
        for (const product of searchResult) {
          const productId = product.value || product.code || product.id || product.sku || ''
          if (productId === id) {
            foundProductsMap.set(productId, product)
            found = true
            break
          }
        }

        // If no exact match, check if any product contains the ID
        if (!found && searchResult.length > 0) {
          // Take the first result if search returned something
          const firstProduct = searchResult[0]
          const productId =
            firstProduct.value || firstProduct.code || firstProduct.id || firstProduct.sku || ''

          // Verify it's actually related to our search
          if (
            productId.includes(id) ||
            (firstProduct.name && firstProduct.name.includes(id)) ||
            (firstProduct.label && firstProduct.label.includes(id))
          ) {
            foundProductsMap.set(productId, firstProduct)
            found = true
          }
        }

        if (!found) {
          notFoundIds.push(id)
        }
      })

      // Convert map to array of unique products
      const allFoundProducts = Array.from(foundProductsMap.values())

      // Automatically select all found products
      if (allFoundProducts.length > 0) {
        // For EQUAL or NOT_EQUAL operators, only select the first product
        if (operator === 'EQUAL' || operator === 'NOT_EQUAL') {
          // Move any currently selected products back to available
          setAvailableProducts((prev) => [...prev, ...tempSelected])
          // Select only the first found product
          setTempSelected([allFoundProducts[0]])
          message.success(`Found and selected 1 product (single selection mode)`)
        } else {
          // Add to selected products (avoiding duplicates)
          setTempSelected((prev) => {
            const existingIds = new Set(prev.map((p) => p.value || p.code || p.id || ''))
            const newProducts = allFoundProducts.filter((p) => {
              const pid = p.value || p.code || p.id || ''
              return pid && !existingIds.has(pid)
            })
            return [...prev, ...newProducts]
          })
          message.success(`Found and selected ${allFoundProducts.length} products`)
        }
      }

      if (notFoundIds.length > 0) {
        message.warning(
          `${notFoundIds.length} product IDs not found: ${notFoundIds.slice(0, 3).join(', ')}${
            notFoundIds.length > 3 ? '...' : ''
          }`
        )
      }

      // Clear import text
      setImportText('')

      // Reset search to show all products
      onSearch('')
    } catch (error) {
      message.error('Error importing products')
      console.error('Import error:', error)
    } finally {
      setImportLoading(false)
    }
  }

  const moveToSelected = (product: Product) => {
    // For EQUAL or NOT_EQUAL operators, only allow single selection
    if (operator === 'EQUAL' || operator === 'NOT_EQUAL') {
      // Move any currently selected products back to available
      setAvailableProducts((prev) => {
        const newAvailable = prev.filter(
          (p) => (p.value || p.code || p.id) !== (product.value || product.code || product.id)
        )
        // Add back the previously selected products
        return [...newAvailable, ...tempSelected]
      })
      // Set only the new product as selected
      setTempSelected([product])
    } else {
      // Multi-select behavior for other operators
      setAvailableProducts((prev) =>
        prev.filter(
          (p) => (p.value || p.code || p.id) !== (product.value || product.code || product.id)
        )
      )
      setTempSelected((prev) => [...prev, product])
    }
  }

  const moveToAvailable = (product: Product) => {
    setTempSelected((prev) =>
      prev.filter(
        (p) => (p.value || p.code || p.id) !== (product.value || product.code || product.id)
      )
    )
    setAvailableProducts((prev) => [...prev, product])
  }

  const getFilteredAvailable = () => {
    if (!availableSearch) return availableProducts
    const search = availableSearch.toLowerCase()
    return availableProducts.filter(
      (p) =>
        (p.name || p.label || '').toLowerCase().includes(search) ||
        (p.value || p.code || p.id || '').includes(search) ||
        (p.sku || '').toLowerCase().includes(search)
    )
  }

  const getFilteredSelected = () => {
    return tempSelected
  }

  const handleConfirm = () => {
    onSelect(tempSelected)
  }

  const handleClear = () => {
    setTempSelected([])
    setAvailableProducts((prev) => [...prev, ...tempSelected])
  }

  const renderProduct = (product: Product, onClick: () => void) => {
    const productId = product.value || product.code || product.id || product.sku || ''
    return (
      <div
        key={productId}
        style={{
          ...STYLES.productItem,
          ...(hoveredProduct === productId ? STYLES.productItemHover : {}),
        }}
        onMouseEnter={() => setHoveredProduct(productId)}
        onMouseLeave={() => setHoveredProduct(null)}
        onClick={onClick}
      >
        {product.image ? (
          <img
            src={product.image}
            alt={product.name || product.label}
            style={STYLES.productImage}
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <DefaultProductImage size={24} />
        )}
        <div style={STYLES.productInfo}>
          <div style={STYLES.productName}>{product.name || product.label}</div>
          <div style={STYLES.productId}>{productId}</div>
        </div>
      </div>
    )
  }

  // Load more when scrolling in available products
  const handleAvailableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !loading) {
      onLoadMore()
    }
  }

  return (
    <div style={STYLES.container}>
      {/* Import Section - only show for multi-select operators */}
      {operator !== 'EQUAL' && operator !== 'NOT_EQUAL' && (
        <div style={STYLES.importSection}>
          <div style={STYLES.importLabel}>
            Paste product IDs (one per line or separated by comma/space):
          </div>
          <div style={STYLES.importExample}>Example: 10840, 10487, 8936190860716, etc.</div>
          <TextArea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder="Enter product IDs here..."
            rows={2}
            disabled={importLoading}
          />
          <Button
            type="primary"
            onClick={handleImport}
            loading={importLoading}
            style={{ marginTop: 8 }}
            size="small"
          >
            Import Products
          </Button>
        </div>
      )}

      {/* Search for available products */}
      <Input
        placeholder="Search all products..."
        value={availableSearch}
        onChange={(e) => {
          setAvailableSearch(e.target.value)
        }}
        style={{ marginBottom: 12 }}
      />

      {/* Two Panel Layout */}
      <Spin spinning={loading || importLoading}>
        <div style={STYLES.panelContainer}>
          {/* Available Products Panel */}
          <div style={STYLES.panel}>
            <div style={STYLES.panelHeader}>
              Available Products ({getFilteredAvailable().length})
            </div>
            <div style={STYLES.productList} onScroll={handleAvailableScroll}>
              {getFilteredAvailable().length === 0 ? (
                <Empty description="No available products" />
              ) : (
                getFilteredAvailable().map((product) =>
                  renderProduct(product, () => moveToSelected(product))
                )
              )}
            </div>
          </div>

          {/* Selected Products Panel */}
          <div style={STYLES.panel}>
            <div style={STYLES.panelHeader}>Selected Products ({getFilteredSelected().length})</div>
            <div style={STYLES.productList}>
              {getFilteredSelected().length === 0 ? (
                <Empty description="No selected products" />
              ) : (
                getFilteredSelected().map((product) =>
                  renderProduct(product, () => moveToAvailable(product))
                )
              )}
            </div>
          </div>
        </div>
      </Spin>

      {/* Footer */}
      <div style={STYLES.footer}>
        <Button onClick={handleClear} disabled={tempSelected.length === 0}>
          Clear All
        </Button>
        <div style={STYLES.footerButtons}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" onClick={handleConfirm}>
            OK ({tempSelected.length} selected)
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ProductTwoPanelSelector
